import {
   GeoJSONFeature,
   LayerSpecification,
   LngLatBounds,
   MapMouseEvent,
   Point,
   Popup,
   SourceSpecification
} from 'mapbox-gl'
import { observer } from 'mobx-react'
import React, { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useMap } from 'react-map-gl'
import cn from 'classnames'

import { Loader } from '@flowwai-frontend/shared/common-components'

import { MapsInstanceContextProvider } from '../../hooks/useMapsInstanceContext'
import { MapCoordinates, MapStyleEnum } from '../../types'

import * as Styles from './styles'

interface Props extends PropsWithChildren {
   mapStyle: MapStyleEnum
   zoomValue: number
   isEditMode: boolean
   onChangeMapStyle: (mapStyle: MapStyleEnum) => void
   onChangeZoomValue: (zoomValue: number) => void
   setIsEditMode: (value: boolean) => void
}

const MapboxMapContextProvider = (props: Props): React.ReactElement => {
   const { mapStyle, onChangeMapStyle, zoomValue, onChangeZoomValue } = props

   const { current: mapInstance } = useMap()

   const onClickMapRef = useRef<(mapEvent: MapMouseEvent) => void>()

   const [isStylesLoaded, setIsStylesLoaded] = useState(false)

   useEffect(() => {
      const onStyleLoad = () => {
         setIsStylesLoaded(true)
      }

      mapInstance?.on('style.load', onStyleLoad)

      return () => {
         mapInstance?.off('style.load', onStyleLoad)
      }
   }, [])

   if (!mapInstance) return <></>

   const panToCoordinates = (coordinates: MapCoordinates): void => {
      mapInstance.flyTo({ center: [coordinates.lng, coordinates.lat] })
   }

   const fitBounds = (
      coordinatesList: MapCoordinates[],
      shouldAnimate?: boolean
   ): void => {
      const bounds = new LngLatBounds()
      coordinatesList?.forEach(({ lat, lng }) => bounds.extend([lng, lat]))

      mapInstance.fitBounds(bounds, {
         padding: 50,
         maxZoom: 18,
         duration: shouldAnimate ? 500 : 0,
         pitch: 0
      })
   }

   const registerOnClick = (
      onClick: (coordinates: MapCoordinates, point: Point) => void
   ): void => {
      const onClickMap = (mapEvent: MapMouseEvent): void => {
         const newCoordinate = {
            lat: mapEvent.lngLat.lat,
            lng: mapEvent.lngLat.lng
         }

         onClick(newCoordinate, mapEvent.point)
      }

      onClickMapRef.current = onClickMap

      mapInstance.on('click', onClickMap)
   }

   const unRegisterOnClick = (): void => {
      if (onClickMapRef.current) mapInstance.off('click', onClickMapRef.current)
   }

   const addSource = (sourceId: string, source: SourceSpecification): void => {
      const hasSource = mapInstance.getMap().getSource(sourceId)
      if (!hasSource) mapInstance.getMap().addSource(sourceId, source)
   }

   const removeSource = (sourceId: string): void => {
      if (mapInstance.getMap().getSource(sourceId))
         mapInstance.getMap().removeSource(sourceId)
   }
   const addLayer = (layer: LayerSpecification): void => {
      mapInstance.getMap().addLayer(layer)
   }

   const removeLayer = (layerId: string): void => {
      if (mapInstance.getMap().getLayer(layerId))
         mapInstance.getMap().removeLayer(layerId)
   }

   const getFeaturesForPoint = (
      point: Point,
      layers?: string[]
   ): GeoJSONFeature[] =>
      mapInstance.queryRenderedFeatures(point, {
         layers
      })

   const addPopupToMap = (popup: Popup): void => {
      popup.addTo(mapInstance.getMap())
   }

   const renderLoader = (): React.ReactElement => (
      <div className={cn(Styles.loaderContainerStyles)}>
         <Loader />
      </div>
   )

   return (
      <MapsInstanceContextProvider
         isEditMode={props.isEditMode}
         setIsEditMode={props.setIsEditMode}
         panTo={panToCoordinates}
         fitBounds={fitBounds}
         registerOnClick={registerOnClick}
         unRegisterOnClick={unRegisterOnClick}
         mapStyle={mapStyle}
         onChangeMapStyle={onChangeMapStyle}
         zoomValue={zoomValue}
         isStylesLoaded={isStylesLoaded}
         onChangeZoomValue={onChangeZoomValue}
         addSource={addSource}
         removeSource={removeSource}
         addLayer={addLayer}
         removeLayer={removeLayer}
         getFeaturesForPoint={getFeaturesForPoint}
         addPopupToMap={addPopupToMap}
      >
         {isStylesLoaded ? props.children : renderLoader()}
      </MapsInstanceContextProvider>
   )
}

export default observer(MapboxMapContextProvider)
